import { useCallback, useRef, useState } from "react";
import styles from "./OverlapAnalysis.module.css";
import ContentTitle from "../ContentTitle";
import { ContentList } from "../../../../screens/MapScreen";
import Search, { SearchFilters, SearchOptions } from "../../../common/Search";
import { MapItem } from "../../../common/Common";
import ScrollView from "../../ScrollView";
import MapListItem from "../../../common/MapListItem";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

import DataProvider from "../../../../data_provider/DataProvider";
import { MiniButtonType } from "../../MiniButton";
import BottomButtonBox, { ButtomButtonBoxProps } from "../../BottomButtonBox";
import { ListSearchReqBody } from "../../../../data_provider/gateways/MapGateway";
import { MapSearchOptions } from "../../../common/MapListView";
import { title } from "process";

interface OverlapAnalysisProps {
    changeContent: (type: ContentList, reset?: boolean) => void;
}

/**
 * @author mjkang
 * @since 2025-01-02
 */
const OverlapAnalysis = (props: OverlapAnalysisProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [selectedMapId, _setSelectedMapId] = useState<string>();
    const [selectedMapIdRef, setSelectedMapId] = [
        useRef(selectedMapId),
        (mapid: string | undefined) => {
            selectedMapIdRef.current = mapid;
            _setSelectedMapId(mapid);
        },
    ];
    const [searchOptions, setSearchOptions] = useState<SearchOptions>();
    const [selectedIdx, setSelectedIdx] = useState<number>(-1);

    const reqMapList = useCallback(async (reqBody: ListSearchReqBody) => {
        const response = await DataProvider.GetMapList(reqBody);

        if (response.flag && response.result) {
            let _list: MapItem[] = response.result.list;
            
            // 현재 지도 제외
            _list = _list.filter((item) => item.mapId !== params.get("id"));
            
            // 25.09.11 | Kim Young Jun | 이미 오버랩으로 추가된 지도들 제외
            const existingOverlapIds = params.get("overlap")?.split(",").filter(Boolean) || [];
            _list = _list.filter((item) => !existingOverlapIds.includes(item.mapId));
            
            response.result.list = _list;
        }
        return response;
    }, [params]);

    const searchMap = (opt: SearchOptions) => {
        setSearchOptions(opt);
    };

    const searchFilters: SearchFilters[] = [
                {
                    value: "default",
                    name: "전체",
                },
                {
                    value: "title",
                    name: "지도명",
                },
                {
                    value: "description",
                    name: "설명",
                },
                {
                    value: "keyword",
                    name: "키워드",
                },
                {
                    value: "username",
                    name: "별명",
                }
            ];

    const getSearchFilter = (): MapSearchOptions | undefined=> {
        if (searchOptions) {
          const filter = 
                !searchOptions.filter || searchOptions.filter === "default" 
                ? {
                    title: searchOptions.keyword,
                    description: searchOptions.keyword,
                    keyword: searchOptions.keyword,
                    username: searchOptions.keyword,
                } : {
                    [searchOptions.filter]: searchOptions.keyword,
                };
            console.log("getSearchFilter", filter);
            return filter;
        }    
        return undefined;  
    }

    // !@#!-OA1. 오버랩 분석 기능 시작: URL에 overlap 파라미터 추가하여 MapScreen으로 상태 전파
    const startOverlap = () => {
        if (selectedMapIdRef.current) {
            navigate(
                `${location.pathname}?${new URLSearchParams({
                    id: params.get("id") ?? "",
                    overlap: [params.get("overlap"), selectedMapIdRef.current]
                    .filter(Boolean)
                    .join(","),
                })}`
            );
            props.changeContent(ContentList.List);
        }
    };

    const buttonOptions: ButtomButtonBoxProps = {
        left: {
            label: "취소",
            type: MiniButtonType.GRAY,
            onClick: () => {
                props.changeContent(ContentList.Back, true);
            },
        },
        right: {
            label: "확인",
            onClick: startOverlap,
            type: selectedMapId ? MiniButtonType.BLUE : MiniButtonType.DISABLED,
        },
    };

    const renderItem = (item: MapItem, idx: number) => {
        const isActive = idx === selectedIdx;

        const selectMap = (mapid: string) => {
            setSelectedIdx(isActive ? -1 : idx);
            setSelectedMapId(isActive ? undefined : mapid);
        };

        return <MapListItem key={idx} item={item} onClickItem={selectMap} isActive={isActive} />;
    };

    return (
        <div className={`${styles.wrapper} undraggable`}>
            <ContentTitle title={"지도 오버랩"} />
            <div className={styles.container}>
                <div className={styles.top}>
                    <div className={styles.content}>
                        <span className={styles.title}>{"오버랩 지도 선택"}</span>
                        <span className={styles.desc}>{"현재 지도에 오버랩하여 표현할 지도를 선택하세요."}</span>
                    </div>
                    <Search filters={searchFilters} doSearch={searchMap} placeholder="검색어를 입력하세요." type="side" />
                </div>
                <div className={styles.middle}>
                    <ScrollView getData={reqMapList} renderItem={renderItem} filter={getSearchFilter()} />
                </div>
            </div>
            <div className={styles.bottom}>
                <BottomButtonBox {...buttonOptions} />
            </div>
        </div>
    );
};

export default OverlapAnalysis;
